import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, Drawer, IconButton, List, ListItemButton, ListItemText, Typography } from '@mui/material';
import { Close, ExpandMore } from '@mui/icons-material';
import { useAuth } from '@frontend/features/auth/hooks/useAuth';
import { visibleSearchEntries } from '@frontend/features/travel-services/searchNavigation';
import { useSearchAvailability } from '@frontend/features/travel-services/useSearchAvailability';
import { routes } from '@frontend/router/routes';
import { logo } from '@frontend/assets';

export const MobileServiceNavigation = ({
  navigate,
  onFlightsClick,
  flightsOpen,
}: {
  navigate: (route: string) => () => void;
  onFlightsClick: () => void;
  flightsOpen: boolean;
}) => {
  const { ability } = useAuth();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const availability = useSearchAvailability();
  const [open, setOpen] = useState(false);
  const entries = [
    ...(ability?.can('view', 'Dashboard') ? [{ id: 'dashboard', route: routes.dashboard.index }] : []),
    ...visibleSearchEntries((permission) => Boolean(ability?.can('view', permission)), availability),
  ];
  const active = entries.find((entry) =>
    entry.id === 'flights'
      ? flightsOpen || pathname.startsWith('/flights/') || pathname.startsWith('/booking/')
      : pathname === entry.route ||
        pathname.startsWith(`${entry.route}/`) ||
        (entry.id === 'hotels' && pathname.startsWith('/hotel-fares/')),);
  return (
    <>
      <Button
        color="inherit"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={t('common.nav.changeService')}
        sx={{
          minWidth: 0,
          minHeight: 64,
          px: 0,
          alignItems: 'flex-start',
          flexDirection: 'column',
          textTransform: 'none',
        }}
      >
        <Box
          component="img"
          src={logo}
          alt=""
          sx={{ width: 72, height: 24, objectFit: 'contain', objectPosition: 'left' }}
        />
        <Box display="flex" alignItems="center" gap={0.5} minWidth={0}>
          <Typography noWrap fontWeight={600}>
            {t(`common.nav.${active?.id ?? 'changeService'}`)}
          </Typography>
          <ExpandMore />
        </Box>
      </Button>
      <Drawer
        anchor="bottom"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: { maxHeight: '85dvh', borderRadius: '16px 16px 0 0', pb: 'env(safe-area-inset-bottom, 0px)' },
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" px={2} pt={1}>
          <Typography variant="h6">{t('common.nav.changeService')}</Typography>
          <IconButton
            aria-label={t('common.close')}
            onClick={() => setOpen(false)}
            sx={{ minWidth: 44, minHeight: 44 }}
          >
            <Close />
          </IconButton>
        </Box>
        <List>
          {entries.map((entry) => (
            <ListItemButton
              key={entry.id}
              selected={active?.id === entry.id}
              sx={{ minHeight: 52 }}
              onClick={() => {
                setOpen(false);
                if (entry.id === 'flights') onFlightsClick();
                else navigate(entry.route)();
              }}
            >
              <ListItemText primary={t(`common.nav.${entry.id}`)} />
            </ListItemButton>
          ))}
        </List>
      </Drawer>
    </>
  );
};
